import { useMutation, useQuery } from '@tanstack/react-query';
import { STATIC } from '../../lib/queryPresets';
import * as svc from '../../services/portal/portalService';

export const portalKeys = {
  dossiers: ['client-dossiers'] as const,
  dossier: (id: string) => ['client-dossier', id] as const,
  stages: (id: string) => ['client-dossier-stages', id] as const,
  frise: (id: string) => ['client-dossier-frise', id] as const,
  files: (id: string) => ['client-dossier-files', id] as const,
  contact: (id: string) => ['client-contact', id] as const,
  billing: (id: string) => ['client-dossier-billing', id] as const,
};

export function useClientDossiers() {
  return useQuery({
    ...STATIC,
    queryKey: portalKeys.dossiers,
    queryFn: svc.listClientDossiers,
  });
}

export function useClientDossier(dossierId: string | undefined) {
  return useQuery({
    ...STATIC,
    queryKey: portalKeys.dossier(dossierId ?? ''),
    queryFn: () => svc.getClientDossier(dossierId!),
    enabled: !!dossierId,
  });
}

export function useClientFrise(dossierId: string | undefined) {
  return useQuery({
    ...STATIC,
    queryKey: portalKeys.frise(dossierId ?? ''),
    queryFn: () => svc.listClientFrise(dossierId!),
    enabled: !!dossierId,
  });
}

export function useClientDossierStages(dossierId: string | undefined) {
  return useQuery({
    ...STATIC,
    queryKey: portalKeys.stages(dossierId ?? ''),
    queryFn: () => svc.listClientDossierStages(dossierId!),
    enabled: !!dossierId,
  });
}

export function useClientDossierFiles(dossierId: string | undefined) {
  return useQuery({
    ...STATIC,
    queryKey: portalKeys.files(dossierId ?? ''),
    queryFn: () => svc.listClientDossierFiles(dossierId!),
    enabled: !!dossierId,
  });
}

export function useClientContact(contactId: string | undefined) {
  return useQuery({
    ...STATIC,
    queryKey: portalKeys.contact(contactId ?? ''),
    queryFn: () => svc.getClientContact(contactId!),
    enabled: !!contactId,
  });
}

export function useClientFileDownload() {
  return useMutation({
    mutationFn: (fileId: string) => svc.getClientFileDownloadUrl(fileId),
  });
}

export function useSetClientNotifications() {
  return useMutation({
    mutationFn: ({ clientUserId, enabled }: { clientUserId: string; enabled: boolean }) =>
      svc.setClientNotificationsEnabled(clientUserId, enabled),
  });
}

export function useClientBillingDocuments(dossierId: string | undefined) {
  return useQuery({
    ...STATIC,
    queryKey: portalKeys.billing(dossierId ?? ''),
    queryFn: () => svc.listClientBillingDocuments(dossierId!),
    enabled: !!dossierId,
  });
}

export function useClientBillingPdf() {
  return useMutation({
    mutationFn: (documentId: string) => svc.getClientBillingPdfUrl(documentId),
  });
}
